import { useCallback } from 'react';
import { message } from 'antd';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { useCrmStore } from '../stores/crm-store';
import { CrmTaskDTO, TaskStatus, PageResponse } from '../types';

const API_BASE = '/api/crm/tasks';

export const useTasks = () => {
    const {
        tasks, selectedTask, taskTotal,
        isLoading, isSubmitting, error,
        setTasks, setSelectedTask, setLoading, setSubmitting, setError,
    } = useCrmStore();

    const search = useCallback(async (params: {
        assignedTo?: string;
        status?: TaskStatus;
        custNo?: string;
        page?: number;
        size?: number;
    } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await AXIOS_INSTANCE.get<PageResponse<CrmTaskDTO>>(API_BASE, { params });
            setTasks(response.data.content, response.data.totalElements);
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to fetch tasks';
            setError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setError, setTasks]);

    const fetchByCustomer = useCallback(async (branch: string, custNo: string): Promise<CrmTaskDTO[]> => {
        try {
            const response = await AXIOS_INSTANCE.get<CrmTaskDTO[]>(
                `${API_BASE}/customer/${branch}/${custNo}`
            );
            return response.data;
        } catch {
            return [];
        }
    }, []);

    const create = useCallback(async (data: CrmTaskDTO): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.post<CrmTaskDTO>(API_BASE, data);
            message.success('Task created successfully');
            return true;
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to create task';
            setError(msg);
            message.error(msg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError]);

    const update = useCallback(async (id: number, data: CrmTaskDTO): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.put<CrmTaskDTO>(`${API_BASE}/${id}`, data);
            message.success('Task updated');
            return true;
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to update task';
            setError(msg);
            message.error(msg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError]);

    const complete = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        try {
            await AXIOS_INSTANCE.post(`${API_BASE}/${id}/complete`);
            message.success('Task marked as done');
            return true;
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to complete task');
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting]);

    const cancel = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        try {
            await AXIOS_INSTANCE.post(`${API_BASE}/${id}/cancel`);
            message.success('Task cancelled');
            return true;
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to cancel task');
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting]);

    return {
        tasks, selectedTask, taskTotal,
        isLoading, isSubmitting, error,
        setSelectedTask,
        search, fetchByCustomer, create, update, complete, cancel,
    };
};
